import { useState, useRef, useEffect } from 'react'
import {X, Heart, Copy, Check, Upload, Loader2} from 'lucide-react'
import QRCode from 'qrcode'
import { lumi } from '@/lib/lumi'
import { buildPixPayload } from '@/lib/pixPayload'
import toast from 'react-hot-toast'

interface DonationModalProps {
  isOpen: boolean
  onClose: () => void
  pixKey: string
}

type Step = 'form' | 'pix' | 'receipt'

function formatCPF(value: string) {
  return value
    .replace(/\D/g, '')
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}

function formatCurrency(value: string) {
  const numeric = value.replace(/\D/g, '')
  const number = parseInt(numeric || '0', 10) / 100
  return number.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// Componente interno que renderiza o QR Code real num canvas
function PixQRCode({ payload }: { payload: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!canvasRef.current || !payload) return
    setError(false)
    QRCode.toCanvas(canvasRef.current, payload, {
      width: 220,
      margin: 2,
      color: { dark: '#2D2D2D', light: '#FFFFFF' },
      errorCorrectionLevel: 'M',
    }).catch(() => setError(true))
  }, [payload])

  if (error) {
    return (
      <div className="w-[220px] h-[220px] flex items-center justify-center rounded-xl border-2 border-dashed text-xs text-gray-400 text-center p-4" style={{ borderColor: '#8DD0CC' }}>
        Não foi possível gerar o QR Code. Use a chave PIX copiada.
      </div>
    )
  }

  return (
    <div className="rounded-xl overflow-hidden shadow-md border-4" style={{ borderColor: '#DCF0EE' }}>
      <canvas ref={canvasRef} />
    </div>
  )
}

export default function DonationModal({ isOpen, onClose, pixKey }: DonationModalProps) {
  const [step, setStep] = useState<Step>('form')
  const [name, setName] = useState('')
  const [cpf, setCpf] = useState('')
  const [amountRaw, setAmountRaw] = useState('')
  const [copied, setCopied] = useState(false)
  const [copiedPayload, setCopiedPayload] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [receiptUrl, setReceiptUrl] = useState('')
  const [donationId, setDonationId] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [pixPayload, setPixPayload] = useState('')
  const fileRef = useRef<HTMLInputElement>(null)

  const amountValue = parseFloat(amountRaw.replace(/\./g, '').replace(',', '.')) || 0

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAmountRaw(formatCurrency(e.target.value))
  }

  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCpf(formatCPF(e.target.value))
  }

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!pixKey?.trim()) {
      toast.error('Chave PIX não configurada. Entre em contato com a instituição.')
      return
    }
    if (!name.trim()) { toast.error('Informe seu nome completo.'); return }
    if (cpf.replace(/\D/g, '').length !== 11) { toast.error('CPF inválido.'); return }
    if (amountValue < 1) { toast.error('O valor mínimo é R$ 1,00.'); return }

    // Gera o payload PIX real antes de salvar
    const payload = buildPixPayload({
      pixKey: pixKey.trim(),
      beneficiaryName: 'Palacio da Sabedoria',
      city: 'Sao Jose do Serido',
      amount: amountValue,
      description: 'Doacao Palacio Sabedoria',
      txid: `DOA${Date.now().toString().slice(-10)}`,
    })
    setPixPayload(payload)

    setSubmitting(true)
    try {
      const now = new Date().toISOString()
      const created = await lumi.entities.donations.create({
        donorName: name.trim(),
        donorCpf: cpf,
        amount: amountValue,
        pixKey: pixKey,
        status: 'pending',
        creator: 'visitor',
        createdAt: now,
        updatedAt: now,
      })
      setDonationId((created as { _id: string })._id)
      setStep('pix')
    } catch {
      toast.error('Erro ao registrar doação. Tente novamente.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleCopyKey = () => {
    navigator.clipboard.writeText(pixKey).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    })
  }

  const handleCopyPayload = () => {
    navigator.clipboard.writeText(pixPayload).then(() => {
      setCopiedPayload(true)
      setTimeout(() => setCopiedPayload(false), 2500)
    })
  }

  const handleReceiptUpload = async (file: File) => {
    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      toast.error('Envie uma imagem ou PDF do comprovante.')
      return
    }
    setUploading(true)
    try {
      const result = await lumi.tools.file.upload([file])
      const url = result?.[0]?.fileUrl || result?.[0]?.url || ''
      if (!url) throw new Error('URL não retornada')
      setReceiptUrl(url)
    } catch {
      toast.error('Erro ao enviar comprovante. Tente novamente.')
    } finally {
      setUploading(false)
    }
  }

  const handleConfirmReceipt = async () => {
    if (!receiptUrl) { toast.error('Envie o comprovante antes de confirmar.'); return }
    setSubmitting(true)
    try {
      const now = new Date().toISOString()
      await lumi.entities.donations.update(donationId, {
        receiptUrl,
        status: 'pending',
        updatedAt: now,
      })
      setStep('receipt')
      toast.success('Comprovante enviado! Obrigado pela sua doação.')
    } catch {
      toast.error('Erro ao confirmar. Tente novamente.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    onClose()
    setTimeout(() => {
      setStep('form')
      setName('')
      setCpf('')
      setAmountRaw('')
      setCopied(false)
      setCopiedPayload(false)
      setReceiptUrl('')
      setDonationId('')
      setPixPayload('')
    }, 300)
  }

  if (!isOpen) return null

  const inputClass = "w-full px-4 py-2.5 rounded-xl border bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none transition-all"

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in overflow-y-auto"
      style={{ background: 'rgba(45,45,45,0.55)', backdropFilter: 'blur(4px)' }}
    >
      <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden my-4">

        {/* Cabeçalho */}
        <div className="px-6 py-5 flex items-center justify-between border-b border-gray-100 dark:border-gray-800" style={{ background: '#DCF0EE' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(91,189,181,0.22)' }}>
              <Heart size={20} style={{ color: '#5BBDB5' }} />
            </div>
            <div>
              <h2 className="font-bold text-gray-900 text-base leading-tight">Fazer uma Doação</h2>
              <p className="text-xs text-gray-500">PALACIO DA SABEDORIA</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-white/60 transition-all"
            aria-label="Fechar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Indicador de progresso */}
        <div className="flex px-6 pt-4 gap-2">
          {(['form', 'pix', 'receipt'] as Step[]).map((s, i) => (
            <div key={s} className="flex-1">
              <div
                className="h-1 w-full rounded-full transition-all"
                style={{
                  background: step === s
                    ? '#5BBDB5'
                    : i < ['form', 'pix', 'receipt'].indexOf(step)
                    ? '#8DD0CC'
                    : '#e5e7eb',
                }}
              />
            </div>
          ))}
        </div>

        <div className="px-6 py-5">

          {/* ETAPA 1 — Formulário */}
          {step === 'form' && (
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  Nome completo
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Seu nome completo"
                  className={inputClass}
                  style={{ borderColor: '#d1d5db' }}
                  onFocus={e => (e.currentTarget.style.borderColor = '#5BBDB5')}
                  onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  CPF
                </label>
                <input
                  type="text"
                  value={cpf}
                  onChange={handleCpfChange}
                  placeholder="000.000.000-00"
                  inputMode="numeric"
                  className={inputClass}
                  style={{ borderColor: '#d1d5db' }}
                  onFocus={e => (e.currentTarget.style.borderColor = '#5BBDB5')}
                  onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
                  Valor da doação
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-gray-400">R$</span>
                  <input
                    type="text"
                    value={amountRaw}
                    onChange={handleAmountChange}
                    placeholder="0,00"
                    inputMode="numeric"
                    className={`${inputClass} pl-10`}
                    style={{ borderColor: '#d1d5db' }}
                    onFocus={e => (e.currentTarget.style.borderColor = '#5BBDB5')}
                    onBlur={e => (e.currentTarget.style.borderColor = '#d1d5db')}
                    required
                  />
                </div>
              </div>

              {/* Valores sugeridos */}
              <div>
                <p className="text-xs text-gray-400 mb-2">Valores sugeridos:</p>
                <div className="flex gap-2 flex-wrap">
                  {['10,00', '25,00', '50,00', '100,00'].map(v => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setAmountRaw(v)}
                      className="px-3 py-1 rounded-lg text-xs font-semibold border transition-all"
                      style={{
                        borderColor: amountRaw === v ? '#5BBDB5' : '#e5e7eb',
                        color: amountRaw === v ? '#5BBDB5' : '#6b7280',
                        background: amountRaw === v ? '#DCF0EE' : 'transparent',
                      }}
                    >
                      R$ {v}
                    </button>
                  ))}
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 disabled:opacity-60 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 text-sm shadow-sm"
                style={{ background: '#5BBDB5' }}
                onMouseEnter={e => { if (!submitting) e.currentTarget.style.background = '#4aaba3' }}
                onMouseLeave={e => (e.currentTarget.style.background = '#5BBDB5')}
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Heart size={16} />}
                Gerar QR Code PIX
              </button>
            </form>
          )}

          {/* ETAPA 2 — QR Code PIX real */}
          {step === 'pix' && (
            <div className="space-y-4">

              {/* Valor */}
              <div className="text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">Valor a transferir</p>
                <p className="text-3xl font-black text-gray-900 dark:text-white mt-0.5">
                  R$ {amountValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </p>
                <p className="text-xs text-gray-400 mt-0.5">
                  para <strong className="text-gray-600 dark:text-gray-300">PALACIO DA SABEDORIA</strong>
                </p>
              </div>

              {/* QR Code real */}
              <div className="flex flex-col items-center gap-2">
                <PixQRCode payload={pixPayload} />
                <p className="text-xs text-gray-400 text-center">
                  Aponte a câmera do seu app bancário para o QR Code
                </p>
              </div>

              {/* Copiar payload completo (PIX Copia e Cola) */}
              <div className="rounded-xl p-3 border" style={{ background: '#DCF0EE', borderColor: '#8DD0CC' }}>
                <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#1e7a74' }}>
                  PIX Copia e Cola
                </p>
                <div className="flex items-center gap-2">
                  <p className="flex-1 text-xs font-mono text-gray-600 dark:text-gray-300 break-all line-clamp-2">
                    {pixPayload}
                  </p>
                  <button
                    onClick={handleCopyPayload}
                    className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 text-white text-xs font-semibold rounded-lg transition-all"
                    style={{ background: '#5BBDB5' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#4aaba3')}
                    onMouseLeave={e => (e.currentTarget.style.background = '#5BBDB5')}
                  >
                    {copiedPayload ? <Check size={13} /> : <Copy size={13} />}
                    {copiedPayload ? 'Copiado!' : 'Copiar'}
                  </button>
                </div>
              </div>

              {/* Chave PIX manual */}
              <div className="rounded-xl p-3 border bg-white dark:bg-gray-800" style={{ borderColor: '#e5e7eb' }}>
                <p className="text-xs font-semibold text-gray-500 mb-1">Ou use a chave PIX:</p>
                <div className="flex items-center gap-2">
                  <p className="flex-1 text-sm font-mono font-semibold text-gray-900 dark:text-gray-100 break-all">{pixKey}</p>
                  <button
                    onClick={handleCopyKey}
                    className="flex-shrink-0 flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg border transition-all"
                    style={{ borderColor: '#8DD0CC', color: '#1e7a74' }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#DCF0EE' }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent' }}
                  >
                    {copied ? <Check size={12} /> : <Copy size={12} />}
                    {copied ? 'Copiado!' : 'Copiar'}
                  </button>
                </div>
              </div>

              {/* Upload comprovante */}
              <div className="space-y-3 pt-1">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 text-center">
                  Já fez o PIX? Envie o comprovante:
                </p>

                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,application/pdf"
                  className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleReceiptUpload(f) }}
                />

                {receiptUrl ? (
                  <div className="flex items-center gap-2 p-3 rounded-xl border" style={{ background: '#DCF0EE', borderColor: '#8DD0CC' }}>
                    <Check size={16} style={{ color: '#5BBDB5' }} className="flex-shrink-0" />
                    <span className="text-sm font-medium" style={{ color: '#1e7a74' }}>Comprovante enviado!</span>
                    <button onClick={() => setReceiptUrl('')} className="ml-auto text-xs text-gray-400 hover:text-red-500">
                      Trocar
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed rounded-xl text-sm text-gray-500 dark:text-gray-400 transition-all"
                    style={{ borderColor: '#8DD0CC' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#5BBDB5'; e.currentTarget.style.color = '#5BBDB5' }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#8DD0CC'; e.currentTarget.style.color = '' }}
                  >
                    {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
                    {uploading ? 'Enviando...' : 'Selecionar comprovante (imagem ou PDF)'}
                  </button>
                )}

                <button
                  onClick={handleConfirmReceipt}
                  disabled={!receiptUrl || submitting}
                  className="w-full py-3 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 text-sm shadow-sm"
                  style={{ background: '#5BBDB5' }}
                  onMouseEnter={e => { if (receiptUrl && !submitting) e.currentTarget.style.background = '#4aaba3' }}
                  onMouseLeave={e => (e.currentTarget.style.background = '#5BBDB5')}
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                  Confirmar envio do comprovante
                </button>
              </div>
            </div>
          )}

          {/* ETAPA 3 — Sucesso */}
          {step === 'receipt' && (
            <div className="text-center py-4 space-y-4">
              <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center" style={{ background: '#DCF0EE' }}>
                <Check size={36} style={{ color: '#5BBDB5' }} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                  Obrigado, {name.split(' ')[0]}! 💚
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                  Seu comprovante foi recebido e a doação será confirmada em breve. Sua generosidade faz toda a diferença para os nossos idosos.
                </p>
              </div>
              <div className="rounded-xl p-4 border" style={{ background: '#DCF0EE', borderColor: '#8DD0CC' }}>
                <p className="text-xs" style={{ color: '#1e7a74' }}>
                  Doação de <strong>R$ {amountValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong> registrada com sucesso. Guarde o seu comprovante.
                </p>
              </div>
              <button
                onClick={handleClose}
                className="w-full py-3 text-white font-bold rounded-xl transition-all text-sm"
                style={{ background: '#5BBDB5' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#4aaba3')}
                onMouseLeave={e => (e.currentTarget.style.background = '#5BBDB5')}
              >
                Fechar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
